/* Collection updates use Shopify's rendered Liquid so card and promotion logic stays on the server. */
(() => {
  if (window.loulouCollectionFilters) return;
  window.loulouCollectionFilters = true;

  class CollectionFilters {
    constructor(root) {
      this.root = root;
      this.url = this.normalizeFilterURL(new URL(location.href));
      this.renderedURL = new URL(this.url);
      this.events = new AbortController();
      const options = { signal: this.events.signal };
      const header = document.querySelector('.header-group--wrapper');
      if (header) {
        const updateHeaderHeight = () => {
          root.style.setProperty('--collection-header-height', `${header.getBoundingClientRect().height}px`);
        };
        updateHeaderHeight();
        if (window.ResizeObserver) {
          this.headerObserver = new ResizeObserver(updateHeaderHeight);
          this.headerObserver.observe(header);
        }
        window.addEventListener('resize', updateHeaderHeight, options);
      }
      root.addEventListener('change', event => this.change(event), options);
      root.addEventListener('keydown', event => {
        if (event.defaultPrevented || event.key !== ' ' || event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;
        const filter = event.target.closest('a.filter-option[role="menuitemcheckbox"]');
        if (!filter) return;
        event.preventDefault();
        if (!event.repeat) filter.click();
      }, options);
      root.addEventListener('submit', event => {
        if (!event.target.matches('.filter-form')) return;
        event.preventDefault();
        this.navigate(this.url);
      }, options);
      document.addEventListener('click', event => this.click(event), options);
      window.addEventListener('popstate', () => {
        if (location.pathname !== this.renderedURL.pathname) return;
        this.navigate(new URL(location.href), { history: false });
      }, options);
    }

    key(url) {
      const params = [...url.searchParams].sort(([a, av], [b, bv]) => a.localeCompare(b) || av.localeCompare(bv));
      return url.pathname + '?' + new URLSearchParams(params);
    }

    normalizeFilterURL(url) {
      const normalized = new URL(url);
      const choices = new Map();
      this.root.querySelectorAll('input[name^="filter."][data-filter-label], a.filter-option[data-filter-name][data-filter-label]').forEach(node => {
        const name = node.name || node.dataset.filterName;
        const value = node.value || node.dataset.filterValue;
        if (!choices.has(name)) choices.set(name, new Map());
        const values = choices.get(name);
        values.set(value, value);
        values.set(node.dataset.filterLabel, value);
      });
      choices.forEach((values, name) => {
        if (!url.searchParams.has(name)) return;
        // Literal commas separate values; encoded commas belong to a label.
        // Decode each item only after separating the raw URL list.
        const rawValues = url.search.slice(1).split('&').flatMap(pair => {
          if (!new URLSearchParams(pair).has(name)) return [];
          const separator = pair.indexOf('=');
          const encoded = separator < 0 ? '' : pair.slice(separator + 1);
          return encoded.split(',').map(value => new URLSearchParams(`value=${value}`).get('value'));
        });
        const selected = rawValues.flatMap(value => {
          if (values.has(value)) return [values.get(value)];
          const parts = value.split(',');
          return parts.length > 1 && parts.some(part => values.has(part))
            ? parts.map(part => values.get(part) || part) : [value];
        });
        normalized.searchParams.delete(name);
        [...new Set(selected)].forEach(value => normalized.searchParams.append(name, value));
      });
      return normalized;
    }

    setValue(url, name, value, selected) {
      const values = url.searchParams.getAll(name).filter(item => item !== value);
      if (selected) values.push(value);
      url.searchParams.delete(name);
      [...new Set(values)].forEach(item => url.searchParams.append(name, item));
    }

    change(event) {
      const input = event.target;
      if (!input.matches('.filter-form input[type="checkbox"][name^="filter."]')) return;
      const url = new URL(this.url);
      this.setValue(url, input.name, input.value, input.checked);
      // Match the existing form: selecting filters requests only available stock.
      url.searchParams.set('filter.v.availability', '1');
      url.searchParams.delete('page');
      this.navigate(url);
    }

    click(event) {
      if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const retry = event.target.closest('[data-filter-retry]');
      if (retry && this.root.contains(retry)) {
        this.navigate(this.url, { history: this.writeHistory, force: true });
        return;
      }
      const link = event.target.closest('a[href]');
      if (!link || link.hasAttribute('download') || (link.target && link.target !== '_self')) return;
      const category = link.matches('.js-collection-subcategories-item[data-filter-name]');
      if (!this.root.contains(link) && !category) return;
      if (!link.matches('.filter-option, .applied-filter, .clear-all-filters, .sort-option, .paginate-wrapper a, .no-products-message a') && !category) return;
      const href = new URL(link.href);
      if (href.origin !== location.origin || href.pathname !== this.url.pathname) return;
      event.preventDefault();
      if (link.getAttribute('aria-disabled') === 'true' || link.matches('.paginate-wrapper .active, .paginate-wrapper .hidden')) return;

      const url = new URL(this.url);
      const { filterName: name, filterValue: value } = link.dataset;
      let pagination = false;
      if (link.matches('.clear-all-filters, .no-products-message a')) {
        [...url.searchParams.keys()].filter(key => key.startsWith('filter.')).forEach(key => url.searchParams.delete(key));
      } else if (link.matches('.sort-option')) {
        url.searchParams.set('sort_by', href.searchParams.get('sort_by'));
      } else if (link.closest('.paginate-wrapper')) {
        if (!href.searchParams.has('page')) return;
        url.searchParams.set('page', href.searchParams.get('page'));
        pagination = true;
      } else if (name) {
        const selected = url.searchParams.getAll(name).includes(value);
        if (category) url.searchParams.delete(name);
        this.setValue(url, name, value, !link.matches('.applied-filter') && !selected);
        if (link.matches('.filter-option')) url.searchParams.set('filter.v.availability', '1');
      } else {
        return;
      }
      if (!pagination) url.searchParams.delete('page');
      this.navigate(url, { pagination });
    }

    syncControls() {
      this.root.querySelectorAll('.filter-form input[type="checkbox"]').forEach(input => {
        input.checked = this.url.searchParams.getAll(input.name).includes(input.value);
      });
      this.root.querySelectorAll('a.filter-option[data-filter-name]').forEach(link => {
        const selected = this.url.searchParams.getAll(link.dataset.filterName).includes(link.dataset.filterValue);
        link.classList.toggle('active', selected);
        link.setAttribute('aria-checked', String(selected));
      });
      // Keep the separate category carousel and its scroll position intact.
      document.querySelectorAll('.js-collection-subcategories-item[data-filter-name]').forEach(link => {
        const { filterName: name, filterValue: value } = link.dataset;
        const selected = this.url.searchParams.getAll(name).includes(value);
        const url = new URL(this.url);
        url.searchParams.delete(name);
        if (!selected) url.searchParams.set(name, value);
        url.searchParams.delete('page');
        link.href = url.pathname + url.search;
        link.classList.toggle('active', selected);
        if (selected) link.setAttribute('aria-current', 'true');
        else link.removeAttribute('aria-current');
      });
    }

    status(message, failed = false) {
      this.root.querySelectorAll('[data-filter-status]').forEach(node => { node.textContent = message; });
      this.root.querySelectorAll('[data-filter-retry]').forEach(node => { node.hidden = !failed; });
    }

    busy(value) {
      this.root.classList.toggle('is-filtering', value);
      this.root.querySelector('[data-collection-results]').setAttribute('aria-busy', String(value));
    }

    navigate(url, { history: writeHistory = true, pagination = false, force = false } = {}) {
      const next = this.normalizeFilterURL(url);
      next.searchParams.delete('section_id');
      next.searchParams.delete('sections');
      const key = this.key(next);
      if (!force && key === this.pendingKey) return;
      clearTimeout(this.timer);
      this.request?.abort();
      this.request = null;
      this.pendingKey = null;
      this.url = next;
      this.writeHistory = writeHistory;
      this.syncControls();
      if (!force && key === this.key(this.renderedURL)) {
        this.busy(false);
        this.status('');
        return;
      }
      this.pendingKey = key;
      this.busy(true);
      this.status('Updating products…');
      // Coalesce rapid input and cancel the previous request immediately, before this delay.
      this.timer = setTimeout(() => this.load(next, { writeHistory, pagination }), 100);
    }

    async load(url, { writeHistory, pagination }) {
      const request = new AbortController();
      this.request = request;
      const requestURL = new URL(url);
      const categories = document.querySelector('[data-collection-subcategories]');
      if (categories) {
        requestURL.searchParams.set('sections', [this.root.dataset.sectionId, categories.dataset.sectionId].join(','));
      } else {
        requestURL.searchParams.set('section_id', this.root.dataset.sectionId);
      }
      const timeout = setTimeout(() => request.abort(), 15000);
      try {
        const response = await fetch(requestURL, { signal: request.signal });
        if (!response.ok) throw new Error(`Collection response: ${response.status}`);
        const sections = categories ? await response.json() : null;
        const html = categories ? sections?.[this.root.dataset.sectionId] : await response.text();
        if (this.request !== request) return;
        if (typeof html !== 'string') throw new Error('Missing collection section');
        const page = new DOMParser().parseFromString(html, 'text/html');
        const nextRoot = page.querySelector('[data-collection-filters]');
        if (!nextRoot || nextRoot.dataset.sectionId !== this.root.dataset.sectionId || !nextRoot.querySelector('[data-collection-results]') || !nextRoot.querySelector('.filter-form') || !nextRoot.querySelector('.mobile-filter-controls')) {
          throw new Error('Incomplete collection response');
        }
        let nextCategories;
        if (categories) {
          const categoryHTML = sections?.[categories.dataset.sectionId];
          if (typeof categoryHTML !== 'string') throw new Error('Missing category section');
          nextCategories = new DOMParser().parseFromString(categoryHTML, 'text/html').querySelector('[data-collection-subcategories]');
          if (!nextCategories || nextCategories.dataset.sectionId !== categories.dataset.sectionId || !nextCategories.querySelector('.js-collection-subcategories-container')) {
            throw new Error('Incomplete category response');
          }
        }
        this.render(nextRoot);
        if (nextCategories) {
          const focusedElement = categories.contains(document.activeElement) ? document.activeElement : null;
          const focusedValue = focusedElement?.dataset.filterValue;
          const focusedDirection = focusedElement?.closest('.js-collection-subcategories-control-btn')?.dataset.direction;
          const scroller = categories.querySelector('.js-collection-subcategories-main-container');
          const scrollLeft = scroller?.scrollLeft || 0;
          scroller?.collectionSubcategoriesDestroy?.();
          const replacement = document.importNode(nextCategories, true);
          window.loulouNavigationFilters?.updateImages(replacement);
          categories.replaceWith(replacement);
          window.collectionSubcategoriesInit?.(replacement, { scrollLeft });
          if (focusedElement) {
            const results = this.root.querySelector('[data-collection-results]');
            const target = focusedValue
              ? [...replacement.querySelectorAll('[data-filter-value]')].find(link => link.dataset.filterValue === focusedValue)
              : [...replacement.querySelectorAll('.js-collection-subcategories-control-btn')].find(button => button.dataset.direction === focusedDirection);
            const visible = () => target && !target.disabled && !target.closest('[hidden], .hidden')
              && getComputedStyle(target).display !== 'none' && getComputedStyle(target).visibility === 'visible';
            (visible() ? target : results).focus({ preventScroll: true });
            if (focusedDirection && target && document.activeElement === results) {
              // Arrow visibility uses a 300ms CSS transition. Restore focus when
              // it settles, unless the user moved on or another response replaced it.
              let timer;
              const cleanup = () => {
                target.removeEventListener('transitionend', retry);
                clearTimeout(timer);
              };
              const retry = () => {
                if (!replacement.isConnected || document.activeElement !== results) {
                  cleanup();
                } else if (visible()) {
                  target.focus({ preventScroll: true });
                  cleanup();
                }
              };
              target.addEventListener('transitionend', retry, { signal: this.events.signal });
              requestAnimationFrame(retry);
              // Also handle reduced motion or a missing transition event.
              timer = setTimeout(() => { retry(); cleanup(); }, 400);
            }
          }
        }
        this.renderedURL = new URL(url);
        if (writeHistory && this.key(new URL(location.href)) !== this.key(url)) {
          window.history.pushState(window.history.state, '', url.pathname + url.search + url.hash);
        }
        const count = nextRoot.querySelector('[data-collection-results]').dataset.productCount;
        this.status(`${count} ${count === '1' ? 'product' : 'products'} found.`);
        if (pagination && !this.root.querySelector('.filter-wrapper.active')) {
          const results = this.root.querySelector('[data-collection-results]');
          results.focus({ preventScroll: true });
          results.scrollIntoView({ block: 'start', behavior: 'instant' });
        }
      } catch (error) {
        if (this.request !== request) return;
        this.status('Products could not be updated. Please try again.', true);
      } finally {
        clearTimeout(timeout);
        if (this.request === request) {
          this.request = null;
          this.pendingKey = null;
          this.busy(false);
        }
      }
    }

    render(nextRoot) {
      const drawer = this.root.querySelector('.filter-wrapper');
      const scrollTop = drawer.scrollTop;
      const focused = document.activeElement;
      const focusInside = this.root.contains(focused);
      const focusKey = focusInside ? {
        id: focused.id, name: focused.name, value: focused.value,
        filterName: focused.dataset.filterName, filterValue: focused.dataset.filterValue,
        filterOption: focused.matches('.filter-option'),
        clear: focused.matches('.clear-all-filters'),
      } : null;
      const groups = [...drawer.querySelectorAll('.collection-filter__group.open')].map(group => group.dataset.filter);
      // Keep the drawer, close button, overlay, and surrounding page mounted.
      ['.filter-form', '.mobile-filter-controls', '[data-collection-results]'].forEach(selector => {
        this.root.querySelector(selector).replaceWith(document.importNode(nextRoot.querySelector(selector), true));
      });
      groups.forEach(name => {
        const group = [...drawer.querySelectorAll('.collection-filter__group')].find(node => node.dataset.filter === name);
        group?.classList.add('open');
        group?.querySelector('.filter__group-inner')?.setAttribute('aria-expanded', 'true');
      });
      this.root.querySelector('.mobile-filter-open').setAttribute('aria-expanded', String(drawer.classList.contains('active')));
      this.syncControls();
      if (focusKey && !focused.isConnected) {
        const candidates = [...this.root.querySelectorAll('input, a, button')];
        const replacement = candidates.find(node => focusKey.name && node.name === focusKey.name && node.value === focusKey.value)
          || candidates.find(node => focusKey.filterName && node.dataset.filterName === focusKey.filterName && node.dataset.filterValue === focusKey.filterValue && node.matches('.filter-option') === focusKey.filterOption)
          || candidates.find(node => focusKey.clear && node.matches('.clear-all-filters'))
          || candidates.find(node => focusKey.id && node.id === focusKey.id);
        const fallback = drawer.classList.contains('active') ? drawer.querySelector('.mobile-filter-close') : this.root.querySelector('[data-collection-results]');
        (replacement || fallback)?.focus({ preventScroll: true });
      }
      drawer.scrollTop = scrollTop;
    }

    destroy() {
      this.headerObserver?.disconnect();
      clearTimeout(this.timer);
      this.request?.abort();
      this.request = null;
      this.events.abort();
    }
  }

  const init = (scope = document) => {
    scope.querySelectorAll('[data-collection-filters]').forEach(root => {
      if (!root.collectionFilters) root.collectionFilters = new CollectionFilters(root);
    });
  };
  init();
  document.addEventListener('shopify:section:load', event => init(event.target));
  document.addEventListener('shopify:section:unload', event => {
    event.target.querySelectorAll('[data-collection-filters]').forEach(root => root.collectionFilters?.destroy());
  });
})();
